/**
 * Sentry's default DOM breadcrumb message is a CSS-selector-style string built from tag name,
 * id, and every CSS-in-JS class on the element (see @sentry/browser-utils htmlTreeAsString).
 * Linaria/emotion generate long hashed class names, so `ui.click` breadcrumbs end up as an
 * unreadable wall of classes instead of telling us which button was actually clicked.
 *
 * This describes the clicked/keypressed element using whichever identifying attribute is most
 * useful (id, data-testid, aria-label, visible text, name), for use in a Sentry `beforeBreadcrumb`.
 */

const INTERACTIVE_SELECTOR = 'button, a, input, select, textarea, [role="button"]'
const MAX_TEXT_LENGTH = 80

function getInteractiveElement(element) {
  if (!element) return null
  if (typeof element.closest === 'function') {
    return element.closest(INTERACTIVE_SELECTOR) || element
  }
  return element
}

/**
 * @param {Element} element
 * @returns {string|undefined} A human-readable description of the element, or undefined if
 *   nothing identifying could be found (in which case the default Sentry message is left as-is).
 */
export function describeElement(element) {
  if (!element || typeof element.getAttribute !== 'function') return undefined

  if (element.id) return `#${element.id}`

  const testId = element.dataset?.testid || element.dataset?.testId
  if (testId) return `[data-testid="${testId}"]`

  const ariaLabel = element.getAttribute('aria-label')
  if (ariaLabel) return `"${ariaLabel}"`

  const text = element.textContent?.trim()?.replace(/\s+/g, ' ')
  if (text) {
    const truncatedText = text.length > MAX_TEXT_LENGTH ? `${text.slice(0, MAX_TEXT_LENGTH)}…` : text
    return `"${truncatedText}"`
  }

  const name = element.getAttribute('name')
  if (name) return `[name="${name}"]`

  return undefined
}

/**
 * Sentry `beforeBreadcrumb` hook: rewrites `ui.click` / `ui.input` breadcrumb messages to
 * identify the interactive element (button/link/input) that was actually clicked, instead of
 * a hashed CSS-in-JS class list. Non-UI breadcrumbs pass through unchanged.
 */
export function describeUiBreadcrumb(breadcrumb, hint) {
  if (!breadcrumb.category?.startsWith('ui.')) return breadcrumb

  const target = getInteractiveElement(hint?.event?.target)
  const description = describeElement(target)
  if (!description) return breadcrumb

  const tag = target.tagName ? target.tagName.toLowerCase() : ''
  return { ...breadcrumb, message: tag ? `${tag}${description}` : description }
}
